import { createSlice } from '@reduxjs/toolkit';

type ActiveChat = {
  lexBot: string | null;
  salesForce: string | null;
};

type AdobeTargetExperiments = {
  [experimentName: string]: {
    isLoading?: boolean;
    error?: unknown;
    value?: string;
  };
};

export type AnalyticsState = {
  data: {
    events: unknown[];
    activeChat: ActiveChat;
    adobeTargetExperiments: AdobeTargetExperiments;
  };
};

export const initialState: AnalyticsState = {
  data: {
    events: [],
    activeChat: {
      lexBot: null,
      salesForce: null
    },
    adobeTargetExperiments: {}
  }
};

export const slice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.data = initialState.data;
    },
    pushEvent: (state, { payload }) => {
      state.data.events = [payload, ...state.data.events];
    },
    setActiveChat: (state, { payload }) => {
      state.data.activeChat = payload;
    },
    setIsLoading: (state, { payload }) => {
      const { experimentName } = payload;

      state.data.adobeTargetExperiments[experimentName] =
        state.data.adobeTargetExperiments[experimentName] || {};
      state.data.adobeTargetExperiments[experimentName].isLoading = true;
    },
    setError: (state, { payload }) => {
      const { experimentName, error } = payload;

      state.data.adobeTargetExperiments[experimentName] =
        state.data.adobeTargetExperiments[experimentName] || {};
      state.data.adobeTargetExperiments[experimentName].isLoading = false;
      state.data.adobeTargetExperiments[experimentName].error = error;
    },
    setSuccess: (state, { payload }) => {
      const { experimentName, experimentValue } = payload;

      state.data.adobeTargetExperiments[experimentName] =
        state.data.adobeTargetExperiments[experimentName] || {};
      state.data.adobeTargetExperiments[experimentName].isLoading = false;
      state.data.adobeTargetExperiments[experimentName].error = undefined;
      state.data.adobeTargetExperiments[experimentName].value = experimentValue;
    }
  }
});

export const {
  setInitialState,
  pushEvent,
  setActiveChat,
  setIsLoading,
  setError,
  setSuccess
} = slice.actions;

export const selectSlice = (state) => state[slice.name];
export const selectSliceData = (state) => selectSlice(state).data;
export const selectEvents = (state) => selectSliceData(state).events;
export const selectActiveChat = (state) => selectSliceData(state).activeChat;
export const selectAdobeTargetExperiments = (state) =>
  selectSliceData(state).adobeTargetExperiments;

export default slice.reducer;
